/**
 * DEV-SPC compact UI: same filtering as AddressCompareDevDialog (spcCompactUi=true)
 * for Residential tab display score counts without opening the modal.
 */

export interface CompareScoreItem {
  index: number;
  address: Record<string, any>;
  scoreVsRecommended: number;
  scoreVsRecommendedPct: string;
  verdict: string;
  pass: boolean;
  avgScore: number;
  avgScorePct: string;
  completeness: number;
  isRecommended: boolean;
  detail?: Record<string, any>;
}

export interface CompareResult {
  total: number;
  recommended: Record<string, any> | null;
  recommendedIndex: number;
  avgScore: number;
  avgScorePct: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  scores: CompareScoreItem[];
  groups: Array<{ canonicalKey: string; members: Array<Record<string, any>>; count: number }>;
  summary: string;
}

function _cleanField(v: any): string {
  if (v == null) return '';
  return String(v)
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();
}

export function fmtAddrLineSpcCompact(a: Record<string, any>): string {
  const parts = [
    _cleanField(a.street1 || a.street01 || a.address1 || a.addressLine1),
    _cleanField(a.street2 || a.street02 || a.address2 || a.addressLine2),
    _cleanField(a.street3 || a.street03 || a.address3 || a.addressLine3),
    _cleanField(a.city),
    _cleanField(a.postcode || a.post_code || a.zip || a.postalCode),
    _cleanField(a.state),
    _cleanField(a.country),
  ].filter((v: string) => v !== '');
  return parts.length ? parts.join(', ') : '(empty)';
}

/**
 * Scores(compact)와 동일한 표시 한 줄(`fmtAddrLineSpcCompact`) 기준 고유 주소 개수.
 * SPC 원천 N행이 같은 주소 문자열이면 1로 집계해 다건 힌트 오탐을 줄인다.
 */
export function countDistinctSpcAddressDisplayLines(rows: Array<Record<string, any>> | undefined | null): number {
  const set = new Set<string>();
  for (const row of rows || []) {
    const line = fmtAddrLineSpcCompact(row).trim();
    if (!line || line === '(empty)') continue;
    set.add(line.toUpperCase());
  }
  return set.size;
}

export function isCountryOnlyAddressSpc(a: Record<string, any> | null | undefined): boolean {
  if (!a || typeof a !== 'object') return false;
  const hasStreet = [
    a.street1, a.street01, a.address1, a.addressLine1,
    a.street2, a.street02, a.address2, a.addressLine2,
    a.street3, a.street03, a.address3, a.addressLine3,
  ].some((v: any) => String(v ?? '').trim() !== '');
  const hasPostCode = String(a.postcode ?? a.post_code ?? a.zip ?? a.postalCode ?? '').trim() !== '';
  const hasCity = String(a.city ?? '').trim() !== '';
  const hasState = String(a.state ?? '').trim() !== '';
  const hasCountry = String(a.country ?? '').trim() !== '';
  return hasCountry && !hasStreet && !hasPostCode && !hasCity && !hasState;
}

export function computeSpcCompactDisplayScores(
  result: CompareResult | null | undefined,
): CompareScoreItem[] {
  const scores = result?.scores || [];
  const nonCountryOnly = scores.filter((s) => !isCountryOnlyAddressSpc(s.address));
  const mismatches = nonCountryOnly.filter((s) => !s.pass);
  const base = mismatches.length > 0
    ? mismatches
    : (nonCountryOnly.length > 0
      ? [[...nonCountryOnly].sort((a, b) => a.index - b.index)[0]].filter(Boolean) as CompareScoreItem[]
      : []);

  const recommendedIndex = result?.recommendedIndex;
  if (typeof recommendedIndex === 'number') {
    const recommendedRow = nonCountryOnly.find((s) => s.index === recommendedIndex);
    if (recommendedRow) {
      base.push(recommendedRow);
    }
  }

  const dedupedByAddress = new Map<string, CompareScoreItem>();
  for (const score of base) {
    const key = fmtAddrLineSpcCompact(score.address).toUpperCase();
    const existing = dedupedByAddress.get(key);
    const existingIsRecommended = !!existing?.isRecommended;
    const incomingIsRecommended = !!score?.isRecommended;
    if (
      !existing ||
      (incomingIsRecommended && !existingIsRecommended) ||
      (!existingIsRecommended && !incomingIsRecommended && score.index < existing.index)
    ) {
      dedupedByAddress.set(key, score);
    }
  }
  return Array.from(dedupedByAddress.values()).sort((a, b) => a.index - b.index);
}
